import { useState, useEffect, useMemo, useCallback, memo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { 
  ArrowUp, ArrowDown, Target, AlertTriangle, 
  TrendingUp, Clock, Trash2, RefreshCw, Check,
  ChevronUp, ChevronDown, ChevronsUpDown, Filter, X, CandlestickChart
} from "lucide-react";
import { TradeChartModal } from "@/components/TradeChartModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fetchContractPrices, getAllCachedPrices, getExchangeForSymbol, loadConfig, getConfig } from "@/services/PriceService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// EXTRACTED COMPONENTS - Moved outside to prevent re-creation
// ============================================================

// Memoized SortableHeader component - prevents re-render on parent state changes
const SortableHeader = memo(({ field, label, sortField, sortDir, onSort }) => {
  const isActive = sortField === field;
  return (
    <TableHead 
      className="text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          sortDir === "asc" ? (
            <ChevronUp className="w-4 h-4 text-profit" />
          ) : (
            <ChevronDown className="w-4 h-4 text-profit" />
          )
        ) : (
          <ChevronsUpDown className="w-3 h-3 opacity-50" />
        )}
      </div>
    </TableHead>
  );
});
SortableHeader.displayName = 'SortableHeader';

// Utility function - defined outside component to avoid recreation
const formatPrice = (price) => {
  if (!price) return "-";
  return `₹${price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
};

export const SignalsDashboard = () => {
  const [signals, setSignals] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [accountsList, setAccountsList] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [prices, setPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  
  // Sorting state
  const [activeSortField, setActiveSortField] = useState("symbol");
  const [activeSortDir, setActiveSortDir] = useState("asc");
  const [newSortField, setNewSortField] = useState("symbol");
  const [newSortDir, setNewSortDir] = useState("asc");
  
  // Filter state
  const [filterAccount, setFilterAccount] = useState("all");
  const [filterStrategy, setFilterStrategy] = useState("all");
  const [filterSymbol, setFilterSymbol] = useState("all");
  const [filterDirection, setFilterDirection] = useState("all");
  
  // Trade modal state
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [tradeDirection, setTradeDirection] = useState("");
  const [tradeForm, setTradeForm] = useState({
    symbol: "",
    exchange: "NSE",
    direction: "",
    entry_price: "",
    entry_date: "",
    num_lots: 1,
    lot_size: 1,
    account_id: "",
    strategy_id: "",
    stop_loss: "",
    target: "",
    monthly_trend: "",
    entry_comments: ""
  });
  const [submittingTrade, setSubmittingTrade] = useState(false);

  // Live chart popup state
  const [chartConfig, setChartConfig] = useState(null);

  const openSignalChart = (signal, mode) => {
    const levels = [];
    if (mode === "NEW") {
      if (signal.new_long_price > 0) levels.push({ price: signal.new_long_price, title: "LONG NEW", color: "#10b981", dashed: false });
      if (signal.new_short_price > 0) levels.push({ price: signal.new_short_price, title: "SHORT NEW", color: "#f43f5e", dashed: false });
    } else {
      const isShort = (signal.direction || "").toUpperCase() === "SHORT";
      if (signal.entry_price > 0) levels.push({ price: signal.entry_price, title: isShort ? "SHORT" : "LONG", color: isShort ? "#f43f5e" : "#10b981", dashed: false });
      if (signal.sl_price > 0) levels.push({ price: signal.sl_price, title: `SL${signal.sl_order_type ? " · " + signal.sl_order_type : ""}`, color: "#ef4444", dashed: true });
      if (signal.target_price > 0) levels.push({ price: signal.target_price, title: "TARGET", color: "#22c55e", dashed: true });
    }
    const refPrice = signal.entry_price || signal.new_long_price || signal.new_short_price || signal.sl_price || null;
    setChartConfig({
      symbol: signal.symbol,
      exchange: signal.exchange || getExchangeForSymbol(signal.symbol),
      expiry: signal.contract_expiry || null,
      refPrice,
      levels,
      subtitle: mode === "NEW"
        ? `New trade signal · ${signal.strategy || "SpeedBot"}`
        : `${(signal.direction || "").toUpperCase()} position · SL ${signal.sl_order_type || "SL"}`,
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [signalsRes, accountsRes, strategiesRes] = await Promise.all([
        api.get("/trade-signals"),
        api.get("/accounts"),
        api.get("/strategies")
      ]);
      
      setSignals(signalsRes.data);
      setAccountsList(accountsRes.data);
      setStrategies(strategiesRes.data);
      
      // Create accounts lookup
      const accountsMap = {};
      accountsRes.data.forEach(acc => {
        accountsMap[acc.id] = acc.name;
      });
      setAccounts(accountsMap);
      
      // Fetch prices for all signals using the centralized price service
      fetchPricesForSignals(signalsRes.data, false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load signals");
    } finally {
      setLoading(false);
    }
  };

  // Use centralized price service for signals
  const fetchPricesForSignals = useCallback(async (signalsList, forceRefresh = false) => {
    if (!signalsList || signalsList.length === 0) return;
    
    // First, immediately show cached prices if available
    const cachedPrices = getAllCachedPrices();
    if (Object.keys(cachedPrices).length > 0 && !forceRefresh) {
      const priceMap = {};
      signalsList.forEach(signal => {
        const exchange = signal.exchange || getExchangeForSymbol(signal.symbol);
        const key = `${exchange}-${signal.symbol}`;
        if (cachedPrices[key] !== undefined) {
          priceMap[signal.symbol] = cachedPrices[key];
        }
      });
      if (Object.keys(priceMap).length > 0) {
        setPrices(prev => ({ ...prev, ...priceMap }));
      }
    }
    
    setLoadingPrices(true);
    try {
      // Build contracts from signals with proper exchange detection
      const contracts = signalsList.map(signal => ({
        symbol: signal.symbol,
        exchange: signal.exchange || getExchangeForSymbol(signal.symbol),
        contract_expiry: signal.contract_expiry || null
      }));
      
      // Fetch using centralized service
      const fetchedPrices = await fetchContractPrices(contracts, forceRefresh);
      
      // Store prices with just symbol as key for this component
      const priceMap = {};
      signalsList.forEach(signal => {
        const exchange = signal.exchange || getExchangeForSymbol(signal.symbol);
        const key = `${exchange}-${signal.symbol}`;
        if (fetchedPrices[key] !== undefined) {
          priceMap[signal.symbol] = fetchedPrices[key];
        }
      });
      
      setPrices(prev => ({ ...prev, ...priceMap }));
    } catch (error) {
      console.error("Error fetching prices:", error);
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  // Legacy fetchPrices for backward compatibility
  const fetchPrices = async (symbols) => {
    if (!symbols.length) return;
    
    setLoadingPrices(true);
    const priceMap = { ...prices };  // Preserve existing prices
    
    // Symbol name mapping for index futures
    const symbolMapping = {
      "NIFTY 50": "NIFTY",
      "NIFTY BANK": "BANKNIFTY",
      "NIFTY FIN SERVICE": "FINNIFTY",
      "BANK NIFTY": "BANKNIFTY",
      "FIN NIFTY": "FINNIFTY",
      "MIDCAP NIFTY": "MIDCPNIFTY",
    };
    
    // MCX commodities list
    const mcxSymbols = ["GOLD", "GOLDM", "GOLDPETAL", "SILVER", "SILVERM", "SILVERMIC", 
                        "COPPER", "CRUDEOIL", "CRUDEOILM", "NATURALGAS", "ZINC", "ALUMINIUM", 
                        "LEAD", "NICKEL", "MENTHAOIL", "COTTON"];
    
    // Group symbols by exchange for batch fetching
    const nseSymbols = [];
    const mcxSymbolsList = [];
    const symbolToOriginal = {};  // Map API symbol back to original
    
    symbols.forEach(symbol => {
      const mappedSymbol = symbolMapping[symbol] || symbol.replace(/\s+/g, "");
      const isMCX = mcxSymbols.some(mcx => mappedSymbol.toUpperCase().includes(mcx));
      symbolToOriginal[mappedSymbol] = symbol;
      
      if (isMCX) {
        mcxSymbolsList.push(mappedSymbol);
      } else {
        nseSymbols.push(mappedSymbol);
      }
    });
    
    try {
      // Fetch NSE symbols in one batch
      if (nseSymbols.length > 0) {
        try {
          console.log("Fetching NSE prices for:", nseSymbols);
          console.log("symbolToOriginal mapping:", symbolToOriginal);
          
          const response = await api.post("/market-price", {
            exchange: "NSE",
            symbols: nseSymbols
          });
          
          console.log("NSE price response:", response.data);
          
          if (response.data.status && response.data.data) {
            response.data.data.forEach(item => {
              console.log("Processing item:", item);
              const originalSymbol = symbolToOriginal[item.symbol];
              console.log(`Mapping ${item.symbol} -> ${originalSymbol}, LTP: ${item.ltp}`);
              if (item.ltp !== null && item.ltp !== undefined) {
                // Store with both original symbol and API symbol as keys
                if (originalSymbol) {
                  priceMap[originalSymbol] = item.ltp;
                  console.log(`Set price for ${originalSymbol}: ${item.ltp}`);
                }
                // Also store with the API symbol directly
                priceMap[item.symbol] = item.ltp;
                console.log(`Set price for ${item.symbol}: ${item.ltp}`);
              }
            });
          }
        } catch (error) {
          console.error("Error fetching NSE prices:", error);
        }
      }
      
      // Fetch MCX symbols in one batch
      if (mcxSymbolsList.length > 0) {
        try {
          const response = await api.post("/market-price", {
            exchange: "MCX",
            symbols: mcxSymbolsList
          });
          
          if (response.data.status && response.data.data) {
            response.data.data.forEach(item => {
              const originalSymbol = symbolToOriginal[item.symbol];
              if (item.ltp !== null && item.ltp !== undefined) {
                // Store with both original symbol and API symbol as keys
                if (originalSymbol) {
                  priceMap[originalSymbol] = item.ltp;
                }
                priceMap[item.symbol] = item.ltp;
              }
            });
          }
        } catch (error) {
          console.error("Error fetching MCX prices:", error);
        }
      }
    } catch (error) {
      console.error("Error in batch price fetch:", error);
    }
    
    setPrices(priceMap);
    setLoadingPrices(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    // Force refresh prices
    if (signals.length > 0) {
      await fetchPricesForSignals(signals, true);
    }
    setRefreshing(false);
    toast.success("Signals and prices refreshed");
  };

  // Periodic price refresh
  useEffect(() => {
    if (signals.length > 0) {
      // Load config and set up interval to refresh prices
      const setupInterval = async () => {
        await loadConfig();
        const config = getConfig();
        const refreshInterval = config.REFRESH_INTERVAL_MS || 15000;
        console.log(`Signals price refresh interval: ${refreshInterval}ms`);
        
        const interval = setInterval(() => {
          fetchPricesForSignals(signals, true);
        }, refreshInterval);
        
        return interval;
      };
      
      let intervalId;
      setupInterval().then(id => intervalId = id);
      
      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }
  }, [signals, fetchPricesForSignals]);

  // Memoized delete handler
  const handleDelete = useCallback(async (signalId) => {
    if (!window.confirm("Delete this signal?")) return;
    
    try {
      await api.delete(`/trade-signals/${signalId}`);
      toast.success("Signal deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete signal");
    }
  }, []);

  // Memoized trade taken handler
  const handleTradeTaken = useCallback((signal, direction) => {
    // Open modal with pre-filled data
    const currentPrice = prices[signal.symbol];
    const isMCX = ["GOLD", "GOLDM", "SILVER", "SILVERM", "COPPER", "CRUDEOIL", "CRUDEOILM", "NATURALGAS"].some(
      mcx => signal.symbol.toUpperCase().includes(mcx)
    );
    
    const quantity = direction === "LONG" 
      ? signal.new_long_quantity 
      : signal.new_short_quantity;
    
    const entryPrice = direction === "LONG"
      ? signal.new_long_price
      : signal.new_short_price;
    
    setSelectedSignal(signal);
    setTradeDirection(direction);
    setTradeForm({
      symbol: signal.symbol,
      exchange: isMCX ? "MCX" : "NSE",
      direction: direction,
      entry_price: entryPrice || currentPrice || "",
      entry_date: new Date().toISOString().slice(0, 16),
      num_lots: 1,
      lot_size: quantity || 1,
      account_id: signal.account_id || "",
      strategy_id: "",
      stop_loss: signal.sl_price || "",
      target: signal.target_price || "",
      monthly_trend: signal.monthly_trend || "",
      entry_comments: `From signal: ${signal.strategy || 'N/A'}`
    });
    setShowTradeModal(true);
  }, [prices]);

  // Memoized form change handler
  const handleTradeFormChange = useCallback((field, value) => {
    setTradeForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmitTrade = async () => {
    if (!tradeForm.symbol || !tradeForm.entry_price || !tradeForm.direction) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSubmittingTrade(true);
    try {
      // Create the trade
      const tradeData = {
        ...tradeForm,
        entry_price: parseFloat(tradeForm.entry_price),
        num_lots: parseInt(tradeForm.num_lots) || 1,
        lot_size: parseInt(tradeForm.lot_size) || 1,
        stop_loss: tradeForm.stop_loss ? parseFloat(tradeForm.stop_loss) : null,
        target: tradeForm.target ? parseFloat(tradeForm.target) : null,
        strategy_id: tradeForm.strategy_id || null
      };
      
      await api.post("/trades", tradeData);
      
      // Update signal status
      if (selectedSignal) {
        await api.put(`/trade-signals/${selectedSignal.id}`, {
          ...selectedSignal,
          trade_taken: true,
          signal_status: "TRADE_TAKEN"
        });
      }
      
      toast.success(`${tradeForm.direction} trade created for ${tradeForm.symbol}!`);
      setShowTradeModal(false);
      fetchData();
    } catch (error) {
      console.error("Error creating trade:", error);
      toast.error("Failed to create trade: " + (error.response?.data?.detail || error.message));
    } finally {
      setSubmittingTrade(false);
    }
  };

  // Get unique strategies from signals for filter dropdown
  const signalStrategies = useMemo(() => {
    const strats = [...new Set(signals.map(s => s.strategy).filter(Boolean))];
    return strats.sort();
  }, [signals]);

  // Get unique symbols from signals for filter dropdown
  const uniqueSymbols = useMemo(() => {
    return [...new Set(signals.map(s => s.symbol))].sort();
  }, [signals]);

  // Separate and filter signals by type
  const { activePositions, newTradeSignals } = useMemo(() => {
    let filtered = signals;
    
    // Apply filters
    if (filterAccount !== "all") {
      filtered = filtered.filter(s => s.account_id === filterAccount);
    }
    if (filterStrategy !== "all") {
      filtered = filtered.filter(s => s.strategy === filterStrategy);
    }
    if (filterSymbol !== "all") {
      filtered = filtered.filter(s => s.symbol === filterSymbol);
    }
    if (filterDirection !== "all") {
      filtered = filtered.filter(s => s.direction === filterDirection);
    }
    
    const active = filtered.filter(s => 
      s.signal_type === "UPDATE_SL" || s.signal_type === "UPDATE_TARGET" || 
      (s.direction && s.sl_price && s.signal_type !== "NEW")
    );
    
    const newSignals = filtered.filter(s => 
      s.signal_type === "NEW" || s.new_long_price || s.new_short_price
    );
    
    return { activePositions: active, newTradeSignals: newSignals };
  }, [signals, filterAccount, filterStrategy, filterSymbol, filterDirection]);

  // Sort active positions
  const sortedActivePositions = useMemo(() => {
    const sorted = [...activePositions];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch (activeSortField) {
        case "symbol":
          aVal = a.symbol || "";
          bVal = b.symbol || "";
          break;
        case "direction":
          aVal = a.direction || "";
          bVal = b.direction || "";
          break;
        case "sl_price":
          aVal = a.sl_price || 0;
          bVal = b.sl_price || 0;
          break;
        case "target_price":
          aVal = a.target_price || 0;
          bVal = b.target_price || 0;
          break;
        case "current_quantity":
          aVal = a.current_quantity || a.sl_quantity || 0;
          bVal = b.current_quantity || b.sl_quantity || 0;
          break;
        case "cmp":
          aVal = prices[a.symbol] || 0;
          bVal = prices[b.symbol] || 0;
          break;
        default:
          aVal = a.symbol || "";
          bVal = b.symbol || "";
      }
      
      if (typeof aVal === "string") {
        return activeSortDir === "asc" 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      return activeSortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [activePositions, activeSortField, activeSortDir, prices]);

  // Sort new trade signals
  const sortedNewSignals = useMemo(() => {
    const sorted = [...newTradeSignals];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch (newSortField) {
        case "symbol":
          aVal = a.symbol || "";
          bVal = b.symbol || "";
          break;
        case "strategy":
          aVal = a.strategy || "";
          bVal = b.strategy || "";
          break;
        case "new_long_price":
          aVal = a.new_long_price || 0;
          bVal = b.new_long_price || 0;
          break;
        case "new_short_price":
          aVal = a.new_short_price || 0;
          bVal = b.new_short_price || 0;
          break;
        case "cmp":
          aVal = prices[a.symbol] || 0;
          bVal = prices[b.symbol] || 0;
          break;
        default:
          aVal = a.symbol || "";
          bVal = b.symbol || "";
      }
      
      if (typeof aVal === "string") {
        return newSortDir === "asc" 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      return newSortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [newTradeSignals, newSortField, newSortDir, prices]);

  // Memoized pending alerts count for stats card
  const pendingAlertsCount = useMemo(() => {
    return signals.filter(s => s.signal_status === "PENDING").length;
  }, [signals]);

  // Memoized sort handlers with useCallback to prevent unnecessary re-renders
  const handleActiveSort = useCallback((field) => {
    setActiveSortField(prev => {
      if (prev === field) {
        setActiveSortDir(d => d === "asc" ? "desc" : "asc");
        return prev;
      }
      setActiveSortDir("asc");
      return field;
    });
  }, []);

  const handleNewSort = useCallback((field) => {
    setNewSortField(prev => {
      if (prev === field) {
        setNewSortDir(d => d === "asc" ? "desc" : "asc");
        return prev;
      }
      setNewSortDir("asc");
      return field;
    });
  }, []);

  // Memoized SL distance calculator
  const getSlDistance = useCallback((signal) => {
    const cmp = prices[signal.symbol];
    if (!cmp || !signal.sl_price) return null;
    
    const distance = signal.direction === "LONG" 
      ? ((cmp - signal.sl_price) / cmp) * 100
      : ((signal.sl_price - cmp) / cmp) * 100;
    
    return distance;
  }, [prices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div data-testid="signals-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl text-white mb-2">
            Signal Dashboard
          </h1>
          <p className="text-slate-400 text-base">
            Monitor active positions and new trade signals
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-signals-btn"
          className="bg-white/10 text-white hover:bg-white/20 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded">
              <Target className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Active Positions</p>
              <p className="text-white text-2xl font-semibold">{activePositions.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-profit/20 rounded">
              <TrendingUp className="w-5 h-5 text-profit" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">New Trade Signals</p>
              <p className="text-white text-2xl font-semibold">{newTradeSignals.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Pending Alerts</p>
              <p className="text-white text-2xl font-semibold">
                {pendingAlertsCount}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded ${loadingPrices ? 'bg-yellow-500/20' : 'bg-profit/20'}`}>
              <RefreshCw className={`w-5 h-5 ${loadingPrices ? 'text-yellow-400 animate-spin' : 'text-profit'}`} />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Prices Loaded</p>
              <p className="text-white text-2xl font-semibold">
                {Object.keys(prices).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-charcoal/30 rounded-md border border-white/5">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-slate-500 text-sm">Filters:</span>
        </div>
        <div className="flex flex-wrap gap-4">
          <Select value={filterSymbol} onValueChange={setFilterSymbol}>
            <SelectTrigger className="w-48 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="filter-symbol">
              <SelectValue placeholder="All Symbols" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Symbols</SelectItem>
              {uniqueSymbols.map(symbol => (
                <SelectItem key={symbol} value={symbol}>{symbol}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterDirection} onValueChange={setFilterDirection}>
            <SelectTrigger className="w-36 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="filter-direction">
              <SelectValue placeholder="All Directions" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Directions</SelectItem>
              <SelectItem value="LONG">LONG</SelectItem>
              <SelectItem value="SHORT">SHORT</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterAccount} onValueChange={setFilterAccount}>
            <SelectTrigger className="w-44 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="filter-account">
              <SelectValue placeholder="All Accounts" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Accounts</SelectItem>
              {accountsList.map(acc => (
                <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStrategy} onValueChange={setFilterStrategy}>
            <SelectTrigger className="w-44 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="filter-strategy">
              <SelectValue placeholder="All Strategies" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Strategies</SelectItem>
              {signalStrategies.map(strat => (
                <SelectItem key={strat} value={strat}>{strat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active Positions with SL/SAR */}
      <div className="bg-charcoal/50 border border-white/10 rounded-md overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Active Positions - Stop Loss / SAR Levels
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Click column headers to sort • Current positions being monitored for SL hits
          </p>
        </div>
        
        {sortedActivePositions.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <SortableHeader field="symbol" label="Symbol" sortField={activeSortField} sortDir={activeSortDir} onSort={handleActiveSort} />
                  <SortableHeader field="direction" label="Direction" sortField={activeSortField} sortDir={activeSortDir} onSort={handleActiveSort} />
                  <SortableHeader field="current_quantity" label="Qty" sortField={activeSortField} sortDir={activeSortDir} onSort={handleActiveSort} />
                  <SortableHeader field="cmp" label="CMP" sortField={activeSortField} sortDir={activeSortDir} onSort={handleActiveSort} />
                  <SortableHeader field="sl_price" label="SL Price" sortField={activeSortField} sortDir={activeSortDir} onSort={handleActiveSort} />
                  <TableHead className="text-slate-400">SL Dist</TableHead>
                  <TableHead className="text-slate-400">SL Type</TableHead>
                  <SortableHeader field="target_price" label="Target" sortField={activeSortField} sortDir={activeSortDir} onSort={handleActiveSort} />
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedActivePositions.map((signal) => {
                  const cmp = prices[signal.symbol];
                  const slDist = getSlDistance(signal);
                  const isNearSl = slDist !== null && slDist < 2;
                  
                  return (
                    <TableRow key={signal.id} className={`border-white/10 ${isNearSl ? 'bg-loss/10' : ''}`}>
                      <TableCell className="min-w-[180px]">
                        <div className="text-sm text-white font-medium">{signal.symbol}</div>
                        <div className="text-xs text-slate-500">
                          {signal.exchange || "NSE"}
                          {signal.account_id && accounts[signal.account_id] && (
                            <span className="ml-1 text-blue-400">· {accounts[signal.account_id]}</span>
                          )}
                          {signal.contract_expiry && <span className="ml-1 text-yellow-500">({signal.contract_expiry})</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`flex items-center gap-1 ${
                          signal.direction === "LONG" ? "text-profit" : "text-loss"
                        }`}>
                          {signal.direction === "LONG" ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )}
                          {signal.direction}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {signal.current_quantity || signal.sl_quantity || "-"}
                      </TableCell>
                      <TableCell className={`font-mono ${loadingPrices ? 'text-slate-500' : 'text-white'}`}>
                        {cmp ? formatPrice(cmp) : (loadingPrices ? "..." : "-")}
                      </TableCell>
                      <TableCell className="text-yellow-400 font-mono">
                        {formatPrice(signal.sl_price)}
                      </TableCell>
                      <TableCell>
                        {slDist !== null && (
                          <span className={`text-xs font-mono ${
                            slDist < 1 ? 'text-loss' : slDist < 2 ? 'text-yellow-400' : 'text-slate-400'
                          }`}>
                            {slDist.toFixed(1)}%
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          signal.sl_order_type === "SAR" 
                            ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                            : "bg-red-500/20 text-red-400 border border-red-500/30"
                        }`}>
                          {signal.sl_order_type || "SL"}
                        </span>
                      </TableCell>
                      <TableCell className="text-profit font-mono">
                        {formatPrice(signal.target_price)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${
                          signal.signal_status === "PENDING"
                            ? "bg-blue-500/20 text-blue-400"
                            : signal.signal_status === "SL_HIT"
                            ? "bg-red-500/20 text-red-400"
                            : signal.signal_status === "TARGET_HIT"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-slate-500/20 text-slate-400"
                        }`}>
                          {signal.signal_status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`chart-btn-active-${signal.id}`}
                            onClick={() => openSignalChart(signal, "ACTIVE")}
                            className="text-slate-400 hover:text-cyan-400 transition-colors"
                            title="View live chart with Entry / SL / Target levels"
                          >
                            <CandlestickChart className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(signal.id)}
                            className="text-slate-400 hover:text-red-400 transition-colors"
                            title="Delete signal"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">
            No active positions with SL levels
          </div>
        )}
      </div>

      {/* New Trade Signals */}
      <div className="bg-charcoal/50 border border-white/10 rounded-md overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-profit" />
            New Trade Signals - Entry Levels
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Click &quot;Take Trade&quot; to create an active trade and mark signal as taken
          </p>
        </div>
        
        {sortedNewSignals.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <SortableHeader field="symbol" label="Symbol" sortField={newSortField} sortDir={newSortDir} onSort={handleNewSort} />
                  <SortableHeader field="strategy" label="Strategy" sortField={newSortField} sortDir={newSortDir} onSort={handleNewSort} />
                  <SortableHeader field="cmp" label="CMP" sortField={newSortField} sortDir={newSortDir} onSort={handleNewSort} />
                  <SortableHeader field="new_long_price" label="Long Entry" sortField={newSortField} sortDir={newSortDir} onSort={handleNewSort} />
                  <TableHead className="text-slate-400">Long Qty</TableHead>
                  <SortableHeader field="new_short_price" label="Short Entry" sortField={newSortField} sortDir={newSortDir} onSort={handleNewSort} />
                  <TableHead className="text-slate-400">Short Qty</TableHead>
                  <TableHead className="text-slate-400">Trend</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedNewSignals.map((signal) => {
                  const cmp = prices[signal.symbol];
                  const longDist = cmp && signal.new_long_price 
                    ? ((signal.new_long_price - cmp) / cmp * 100).toFixed(1)
                    : null;
                  const shortDist = cmp && signal.new_short_price
                    ? ((cmp - signal.new_short_price) / cmp * 100).toFixed(1)
                    : null;
                  
                  return (
                    <TableRow key={signal.id} className="border-white/10">
                      <TableCell className="min-w-[180px]">
                        <div className="text-sm text-white font-medium">{signal.symbol}</div>
                        <div className="text-xs text-slate-500">
                          {signal.exchange || "NSE"}
                          {signal.account_id && accounts[signal.account_id] && (
                            <span className="ml-1 text-blue-400">· {accounts[signal.account_id]}</span>
                          )}
                          {signal.contract_expiry && <span className="ml-1 text-yellow-500">({signal.contract_expiry})</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {signal.strategy || "-"}
                      </TableCell>
                      <TableCell className={`font-mono ${loadingPrices ? 'text-slate-500' : 'text-white'}`}>
                        {cmp ? formatPrice(cmp) : (loadingPrices ? "..." : "-")}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-profit font-mono">{formatPrice(signal.new_long_price)}</span>
                          {longDist && (
                            <span className={`text-xs ${parseFloat(longDist) < 1 ? 'text-profit' : 'text-slate-500'}`}>
                              {longDist > 0 ? `+${longDist}%` : `${longDist}%`}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {signal.new_long_quantity || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-loss font-mono">{formatPrice(signal.new_short_price)}</span>
                          {shortDist && (
                            <span className={`text-xs ${parseFloat(shortDist) < 1 ? 'text-loss' : 'text-slate-500'}`}>
                              {shortDist > 0 ? `-${shortDist}%` : `${shortDist}%`}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {signal.new_short_quantity || "-"}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${
                          signal.monthly_trend === "Bullish"
                            ? "bg-profit/20 text-profit"
                            : signal.monthly_trend === "Bearish"
                            ? "bg-loss/20 text-loss"
                            : "bg-slate-500/20 text-slate-400"
                        }`}>
                          {signal.monthly_trend || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {signal.trade_taken ? (
                            <span className="text-xs text-profit flex items-center gap-1">
                              <Check className="w-3 h-3" /> Taken
                            </span>
                          ) : (
                            <>
                              {signal.new_long_price && (
                                <Button
                                  size="sm"
                                  onClick={() => handleTradeTaken(signal, "LONG")}
                                  className="bg-profit/20 text-profit hover:bg-profit/30 text-xs h-7 px-2"
                                  data-testid={`take-long-${signal.id}`}
                                >
                                  <ArrowUp className="w-3 h-3 mr-1" />
                                  Long
                                </Button>
                              )}
                              {signal.new_short_price && (
                                <Button
                                  size="sm"
                                  onClick={() => handleTradeTaken(signal, "SHORT")}
                                  className="bg-loss/20 text-loss hover:bg-loss/30 text-xs h-7 px-2"
                                  data-testid={`take-short-${signal.id}`}
                                >
                                  <ArrowDown className="w-3 h-3 mr-1" />
                                  Short
                                </Button>
                              )}
                            </>
                          )}
                          <button
                            data-testid={`chart-btn-new-${signal.id}`}
                            onClick={() => openSignalChart(signal, "NEW")}
                            className="text-slate-400 hover:text-cyan-400 transition-colors"
                            title="View live chart with LONG NEW / SHORT NEW entry levels"
                          >
                            <CandlestickChart className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(signal.id)}
                            className="text-slate-400 hover:text-red-400 transition-colors"
                            title="Delete signal"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">
            No new trade signals
          </div>
        )}
      </div>

      {/* Live Chart Popup */}
      <TradeChartModal open={!!chartConfig} onClose={() => setChartConfig(null)} config={chartConfig} />

      {/* Trade Entry Modal */}
      <Dialog open={showTradeModal} onOpenChange={setShowTradeModal}>
        <DialogContent className="bg-charcoal border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              {tradeDirection === "LONG" ? (
                <ArrowUp className="w-5 h-5 text-profit" />
              ) : (
                <ArrowDown className="w-5 h-5 text-loss" />
              )}
              Create {tradeDirection} Trade - {tradeForm.symbol}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Row 1: Symbol & Exchange */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Symbol</Label>
                <Input
                  value={tradeForm.symbol}
                  onChange={(e) => handleTradeFormChange("symbol", e.target.value)}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  data-testid="trade-modal-symbol"
                />
              </div>
              <div>
                <Label className="text-slate-300">Exchange</Label>
                <Select 
                  value={tradeForm.exchange} 
                  onValueChange={(val) => handleTradeFormChange("exchange", val)}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-white mt-1" data-testid="trade-modal-exchange">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="NSE">NSE</SelectItem>
                    <SelectItem value="MCX">MCX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Direction & Entry Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Direction</Label>
                <Select 
                  value={tradeForm.direction} 
                  onValueChange={(val) => handleTradeFormChange("direction", val)}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-white mt-1" data-testid="trade-modal-direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="LONG">LONG</SelectItem>
                    <SelectItem value="SHORT">SHORT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Entry Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tradeForm.entry_price}
                  onChange={(e) => handleTradeFormChange("entry_price", e.target.value)}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  data-testid="trade-modal-entry-price"
                />
              </div>
            </div>

            {/* Row 3: Entry Date */}
            <div>
              <Label className="text-slate-300">Entry Date & Time</Label>
              <Input
                type="datetime-local"
                value={tradeForm.entry_date}
                onChange={(e) => handleTradeFormChange("entry_date", e.target.value)}
                className="bg-black/20 border-white/10 text-white mt-1"
                data-testid="trade-modal-entry-date"
              />
            </div>

            {/* Row 4: Lots & Lot Size */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Number of Lots</Label>
                <Input
                  type="number"
                  min="1"
                  value={tradeForm.num_lots}
                  onChange={(e) => handleTradeFormChange("num_lots", e.target.value)}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  data-testid="trade-modal-num-lots"
                />
              </div>
              <div>
                <Label className="text-slate-300">Lot Size (Quantity)</Label>
                <Input
                  type="number"
                  min="1"
                  value={tradeForm.lot_size}
                  onChange={(e) => handleTradeFormChange("lot_size", e.target.value)}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  data-testid="trade-modal-lot-size"
                />
              </div>
            </div>

            {/* Row 5: Stop Loss & Target */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Stop Loss</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tradeForm.stop_loss}
                  onChange={(e) => handleTradeFormChange("stop_loss", e.target.value)}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  placeholder="Optional"
                  data-testid="trade-modal-stop-loss"
                />
              </div>
              <div>
                <Label className="text-slate-300">Target</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tradeForm.target}
                  onChange={(e) => handleTradeFormChange("target", e.target.value)}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  placeholder="Optional"
                  data-testid="trade-modal-target"
                />
              </div>
            </div>

            {/* Row 6: Account & Strategy */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Account</Label>
                <Select 
                  value={tradeForm.account_id} 
                  onValueChange={(val) => handleTradeFormChange("account_id", val)}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-white mt-1" data-testid="trade-modal-account">
                    <SelectValue placeholder="Select Account" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    {accountsList.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Strategy</Label>
                <Select 
                  value={tradeForm.strategy_id || "none"} 
                  onValueChange={(val) => handleTradeFormChange("strategy_id", val === "none" ? "" : val)}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-white mt-1" data-testid="trade-modal-strategy">
                    <SelectValue placeholder="Select Strategy" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="none">No Strategy</SelectItem>
                    {strategies.map(strat => (
                      <SelectItem key={strat.id} value={strat.id}>{strat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 7: Monthly Trend */}
            <div>
              <Label className="text-slate-300">Monthly Trend</Label>
              <Select 
                value={tradeForm.monthly_trend || "none"} 
                onValueChange={(val) => handleTradeFormChange("monthly_trend", val === "none" ? "" : val)}
              >
                <SelectTrigger className="bg-black/20 border-white/10 text-white mt-1" data-testid="trade-modal-trend">
                  <SelectValue placeholder="Select Trend" />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-white/10">
                  <SelectItem value="none">Not Set</SelectItem>
                  <SelectItem value="Bullish">Bullish</SelectItem>
                  <SelectItem value="Bearish">Bearish</SelectItem>
                  <SelectItem value="Neutral">Neutral</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Row 8: Comments */}
            <div>
              <Label className="text-slate-300">Entry Comments</Label>
              <Textarea
                value={tradeForm.entry_comments}
                onChange={(e) => handleTradeFormChange("entry_comments", e.target.value)}
                className="bg-black/20 border-white/10 text-white mt-1 min-h-[80px]"
                placeholder="Optional notes about this trade..."
                data-testid="trade-modal-comments"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowTradeModal(false)}
              className="border-white/10 text-slate-300 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitTrade}
              disabled={submittingTrade}
              className={`${
                tradeDirection === "LONG" 
                  ? "bg-profit hover:bg-profit/80" 
                  : "bg-loss hover:bg-loss/80"
              } text-white`}
              data-testid="trade-modal-submit"
            >
              {submittingTrade ? "Creating..." : `Create ${tradeDirection} Trade`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignalsDashboard;
